import React from 'react';
import styled from 'astroturf/react';
import { Icon } from 'js/design_system/Icon';
import { Body } from 'js/design_system/Body';
import ThumbUpFilled from 'js/design_system/icons/thumb_up_filled.svg';
import BookmarkFilled from 'js/design_system/icons/bookmark_filled.svg';
import ImpressionWatcher, {
  Props as ImpressionProps,
} from 'js/utils/impressions/ImpressionWatcher';

const Wrapper = styled.div<{ variant?: 'default' | 'dense' }>`
  display: flex;
  align-items: center;
  padding: 4px var(--spacing-xs-8, 8px);
  &:not(.variant-dense) {
    border-top: 1px solid var(--stroke-light, #dddde2);
  }

  &.variant-dense {
    padding: 8px 0 0;
  }
`;

const InteractionButtonWrapper = styled.div<{ variant?: 'default' | 'dense' }>`
  position: relative;
  &.variant-dense {
    display: flex;
  }
`;

const InteractionButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px 4px 6px 6px;
  border-radius: 16px;

  &:hover {
    background-color: var(--brand-colors-blue-200, #eef2f9);
    color: var(--icon-blue, #0645b1);
  }
`;

const InteractionButtonDisabled = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 6px 4px 6px 6px;
  border-radius: 16px;
`;

const IconWrapper = styled.div`
  line-height: 0;
`;

const InteractionCount = styled(Body)`
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  line-height: 20px;
  margin-left: 2px;
`;

const DenseInteractionCount = styled(Body)`
  line-height: 32px;
  min-width: 33px;
`;

const CountSpacer = styled.div`
  min-width: 33px;
`;

const Spacer = styled.div`
  flex: 1;
`;

export const NewsInteractionBar = ({
  isLiked,
  numLikes,
  onLike,
  onUnlike,
  showBookmark = true,
  isBookmarkEnabled = true,
  isBookmarked,
  numBookmarks,
  onBookmark,
  onUnbookmark,
  numComments,
  onAddComment,
  showComment = true,
  impressionPropsGen,
  variant = 'default',
}: {
  isLiked: boolean;
  numLikes: number;
  onLike?: () => void;
  onUnlike?: () => void;
  showBookmark?: boolean;
  isBookmarkEnabled?: boolean;
  isBookmarked: boolean;
  numBookmarks: number;
  onBookmark?: () => void;
  onUnbookmark?: () => void;
  numComments: number;
  onAddComment?: () => void;
  showComment?: boolean;
  impressionPropsGen: (clickTarget?: string) => ImpressionProps;
  variant?: 'default' | 'dense';
}) => {
  const VariantSpacer = variant === 'default' ? Spacer : styled.div``;
  const InteractionCountComponent =
    variant === 'default' ? InteractionCount : DenseInteractionCount;
  const size = variant === 'default' ? 'md' : 'sm';

  const formatNumber = (num: number): string => {
    if (!num) {
      return '0';
    }
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}K`;
    }
    return num.toString();
  };

  const formattedNumLikes =
    variant === 'dense' ? formatNumber(numLikes) : numLikes;
  const formattedNumComments =
    variant === 'dense' ? formatNumber(numComments) : numComments;
  const formattedNumBookmarks =
    variant === 'dense' ? formatNumber(numBookmarks) : numBookmarks;

  return (
    <Wrapper variant={variant}>
      <VariantSpacer />
      <VariantSpacer />
      <InteractionButtonWrapper variant={variant}>
        <ImpressionWatcher {...impressionPropsGen('like-button')}>
          <InteractionButton
            onClick={isLiked ? onUnlike : onLike}
            data-coachmark-step="news-feed-like"
          >
            <IconWrapper>
              {isLiked ? <ThumbUpFilled size={20} /> : <Icon name="thumb_up" />}
            </IconWrapper>
          </InteractionButton>
        </ImpressionWatcher>
        {numLikes > 0 && (
          <InteractionCountComponent size={size}>
            {formattedNumLikes}
          </InteractionCountComponent>
        )}
        {numLikes === 0 && variant === 'dense' && <CountSpacer />}
      </InteractionButtonWrapper>
      {showComment && (
        <>
          <VariantSpacer />
          <InteractionButtonWrapper variant={variant}>
            <ImpressionWatcher {...impressionPropsGen('comment-button')}>
              <InteractionButton
                onClick={onAddComment}
                data-coachmark-step="news-feed-comment"
              >
                <IconWrapper>
                  <Icon name="chat_bubble" />
                </IconWrapper>
              </InteractionButton>
            </ImpressionWatcher>
            {numComments > 0 && (
              <InteractionCountComponent size={size}>
                {formattedNumComments}
              </InteractionCountComponent>
            )}
            {numComments === 0 && variant === 'dense' && <CountSpacer />}
          </InteractionButtonWrapper>
        </>
      )}
      <VariantSpacer />
      {showBookmark && (
        <InteractionButtonWrapper variant={variant}>
          {isBookmarkEnabled ? (
            <ImpressionWatcher {...impressionPropsGen('bookmark-button')}>
              <InteractionButton
                onClick={isBookmarked ? onUnbookmark : onBookmark}
                data-coachmark-step="news-feed-bookmark"
              >
                <IconWrapper>
                  {isBookmarked ? (
                    <BookmarkFilled size={20} />
                  ) : (
                    <Icon name="bookmark" />
                  )}
                </IconWrapper>
              </InteractionButton>
            </ImpressionWatcher>
          ) : (
            <InteractionButtonDisabled disabled={true}>
              <Icon name="bookmark" />
            </InteractionButtonDisabled>
          )}
          {numBookmarks > 0 && (
            <InteractionCountComponent size={size}>
              {formattedNumBookmarks}
            </InteractionCountComponent>
          )}
          {numBookmarks === 0 && variant === 'dense' && <CountSpacer />}
        </InteractionButtonWrapper>
      )}
      {showBookmark && <VariantSpacer />}
      <VariantSpacer />
    </Wrapper>
  );
};
